import { useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { DashboardSectionHeader } from "src/components/common";
import type { OrganicDateFilters } from "src/dashboards/organicMedia/components/Tabs/FacebookContentReport";
import OrganicDateRangePicker from "src/dashboards/organicMedia/components/Tabs/OrganicDateRangePicker";
import type {
  PaidMediaOverviewGranularity,
  PaidMediaOverviewPlatform,
} from "src/services/PaidMediaOverviewAPI";
import { buildPlatformReportMock } from "../../data/paidMediaPlatformReportMockData";
import { overviewPlatformDescriptor } from "../../data/paidMediaOverviewPlatforms";
import ConversionFunnelPanel from "../metaAds/ConversionFunnelPanel";
import { formatOverviewAmount } from "../../utils/paidMediaOverviewFormat";
import { paidMediaLanguage } from "../../utils/paidMediaNumberFormat";
import PlatformReportAiSummary from "./PlatformReportAiSummary";
import PlatformReportKpiGrid from "./PlatformReportKpiGrid";
import PlatformReportTrendsPanel from "./PlatformReportTrendsPanel";
import PlatformReportChartsRow from "./PlatformReportChartsRow";
import PlatformReportTable from "./PlatformReportTable";
import {
  platformReportEntityKeys,
  platformReportSectionHelp,
  PLATFORM_REPORT_METRIC_COLORS,
  PLATFORM_REPORT_TABLE_TABS,
  type PlatformReportTableTab,
} from "./platformReportConfig";
import { PLATFORM_REPORT_CURRENCY } from "./platformReportFormat";
// Shared chart/segmented-control styling lives with the overview dashboard.
import "../overview/PaidMediaOverviewDashboard.scss";
import "./PlatformAdsReport.scss";

interface PlatformAdsReportProps {
  platform: PaidMediaOverviewPlatform;
  dateFilters: OrganicDateFilters;
}

const PlatformAdsReport: FC<PlatformAdsReportProps> = ({ platform, dateFilters }) => {
  const { t } = useTranslation();
  const [granularity, setGranularity] = useState<PaidMediaOverviewGranularity>("daily");
  const [activeTableTab, setActiveTableTab] = useState<PlatformReportTableTab>("campaigns");

  const descriptor = overviewPlatformDescriptor(platform);
  const platformLabel = descriptor ? t(descriptor.labelKey) : "";
  const clickLabel = t(descriptor?.clicksLabelKey ?? "paidMediaOverview.metrics.clicks");
  const purchasesLabel = t(descriptor?.purchasesLabelKey ?? "paidMediaOverview.metrics.purchases");
  const entityKeys = platformReportEntityKeys(platform);

  const report = useMemo(
    () =>
      buildPlatformReportMock(
        platform,
        dateFilters.appliedStartDate,
        dateFilters.appliedEndDate
      ),
    [platform, dateFilters.appliedStartDate, dateFilters.appliedEndDate]
  );


  const funnelSteps = useMemo(
    () => [
      {
        label: t("paidMediaOverview.metrics.impressions"),
        value: report.totals.impressions,
        color: PLATFORM_REPORT_METRIC_COLORS.impressions,
        kind: "count" as const,
      },
      {
        label: clickLabel,
        value: report.totals.clicks,
        color: PLATFORM_REPORT_METRIC_COLORS.clicks,
        kind: "count" as const,
      },
      {
        label: purchasesLabel,
        value: report.totals.purchases,
        color: PLATFORM_REPORT_METRIC_COLORS.conversions,
        kind: "count" as const,
      },
    ],
    [report.totals, clickLabel, purchasesLabel, t]
  );

  const tableTabLabel = (tab: PlatformReportTableTab): string => {
    if (tab === "campaigns") {
      return t("platformAdsReport.subTabs.campaigns");
    }
    return t(tab === "groups" ? entityKeys.groupLabelKey : entityKeys.creativeLabelKey);
  };

  return (
    <div className="paid-media-report">
      <div className="paid-media-report__header-block">
        <DashboardSectionHeader
          className="paid-media-report__section-header"
          divider={false}
          title={t("platformAdsReport.title", { platform: platformLabel })}
          actions={
            <div className="dashboard-section-header__control dashboard-section-header__control--wide">
              <OrganicDateRangePicker
                startDate={dateFilters.draftStartDate}
                endDate={dateFilters.draftEndDate}
                onRangeChange={dateFilters.onApplyDates}
              />
            </div>
          }
        />
        <div className="paid-media-report__subtitle-toolbar">
          <p className="dashboard-section-header__subtitle paid-media-report__header-subtitle">
            {t("platformAdsReport.subtitle", {
              platform: platformLabel,
              currency: PLATFORM_REPORT_CURRENCY,
            })}
          </p>
        </div>
      </div>

      <div className="paid-media-report__content">
        <PlatformReportAiSummary
          platform={platform}
          platformLabel={platformLabel}
          clickLabel={clickLabel}
          purchasesLabel={purchasesLabel}
          report={report}
          startDate={dateFilters.appliedStartDate}
          endDate={dateFilters.appliedEndDate}
        />

        <PlatformReportKpiGrid
          platform={platform}
          totals={report.totals}
          priorTotals={report.priorTotals}
          clickLabel={clickLabel}
        />

        <div className="platform-report__two-col">
          <PlatformReportTrendsPanel
            series={report.series}
            granularity={granularity}
            onGranularityChange={setGranularity}
            clickLabel={clickLabel}
          />
          <ConversionFunnelPanel
            title={t("platformAdsReport.sections.conversionFunnel")}
            tooltipInfo={platformReportSectionHelp(t, "conversionFunnel")}
            steps={funnelSteps}
            formatMoney={value => formatOverviewAmount(value, PLATFORM_REPORT_CURRENCY)}
            numberLocale={paidMediaLanguage()}
          />
        </div>

        <PlatformReportChartsRow campaigns={report.campaigns} />

        <div className="paid-media-table-zone">
          <div className="paid-media-tabs-bar paid-media-tabs-bar--attached">
            {PLATFORM_REPORT_TABLE_TABS.map(tab => (
              <button
                key={tab}
                type="button"
                className={
                  activeTableTab === tab
                    ? "paid-media-tabs-bar__tab paid-media-tabs-bar__tab--active"
                    : "paid-media-tabs-bar__tab"
                }
                onClick={() => setActiveTableTab(tab)}
              >
                {tableTabLabel(tab)}
              </button>
            ))}
          </div>

          {activeTableTab === "campaigns" ? (
            <PlatformReportTable
              sectionHelpKey="campaignPerformance"
              title={t("platformAdsReport.sections.campaignPerformance")}
              rows={report.campaigns}
              nameColumnLabel={t("platformAdsReport.columns.campaign")}
              descriptorColumnLabel={t("platformAdsReport.columns.objective")}
              clickLabel={clickLabel}
              searchPlaceholder={t("metaAdsReport.placeholders.searchCampaigns")}
              searchAriaLabel={t("metaAdsReport.toolbars.campaignsSearch")}
              csvFilename={`${platform}-campaigns.csv`}
            />
          ) : null}

          {activeTableTab === "groups" ? (
            <PlatformReportTable
              sectionHelpKey="adSets"
              title={t("platformAdsReport.sections.groupPerformance", {
                entity: t(entityKeys.groupLabelKey),
              })}
              rows={report.groups}
              nameColumnLabel={t(entityKeys.groupColumnKey)}
              descriptorColumnLabel={t("platformAdsReport.columns.objective")}
              clickLabel={clickLabel}
              searchPlaceholder={t("platformAdsReport.searchGroups", {
                entity: t(entityKeys.groupLabelKey),
              })}
              searchAriaLabel={t("metaAdsReport.toolbars.adSetsSearch")}
              csvFilename={`${platform}-ad-groups.csv`}
            />
          ) : null}

          {activeTableTab === "creatives" ? (
            <PlatformReportTable
              sectionHelpKey="creativePerformance"
              title={t("platformAdsReport.sections.creativePerformance", {
                entity: t(entityKeys.creativeLabelKey),
              })}
              rows={report.creatives}
              nameColumnLabel={t(entityKeys.creativeLabelKey)}
              descriptorColumnLabel={t("platformAdsReport.columns.campaign")}
              clickLabel={clickLabel}
              searchPlaceholder={t("platformAdsReport.searchCreatives", {
                entity: t(entityKeys.creativeLabelKey),
              })}
              searchAriaLabel={t("metaAdsReport.toolbars.adsSearch")}
              csvFilename={`${platform}-creatives.csv`}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PlatformAdsReport;
